// @ts-nocheck
import { useState, useMemo } from "react";
import type { ReactNode } from "react";
import {
  Button, Modal, Form, Input, Select, Switch, Typography,
  message, Tooltip, Dropdown,
} from "../components/admin-ui";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ApiOutlined, WalletOutlined,
  MoreOutlined, CheckCircleOutlined, CloseCircleOutlined,
} from "../components/admin-icons";
import { useTranslation } from "react-i18next";
import type { ColumnsType } from "../components/admin-ui";
import type { PaymentMethodConfig } from "@kawkaw/shared";
import { DataTable } from "../components/DataTable";
import { tokens } from "../styles/tokens";
import { AdminShell } from "../layouts/AdminShell";
import {
  usePaymentMethodConfigs, useCreatePaymentMethodConfig,
  useUpdatePaymentMethodConfig, useDeletePaymentMethodConfig, useTestPaymentMethodConfig,
} from "../api/hooks";

const { Text } = Typography;
const { color: T } = tokens;

function PaymentStatCard({
  label, value, icon, iconColor, badge, badgeColor, badgeBg,
}: {
  label: string;
  value: number;
  icon: ReactNode;
  iconColor: string;
  badge: string;
  badgeColor: string;
  badgeBg: string;
}) {
  return (
    <div className="admin-kpi-card admin-payment-method-stat">
      <div className="admin-kpi-heading flex">
        <div className="admin-kpi-label">{label}</div>
        <span className="admin-kpi-icon" style={{ color: iconColor, background: badgeBg }}>{icon}</span>
      </div>
      <div className="admin-kpi-value">{value.toLocaleString()}</div>
      <div className="admin-payment-method-stat-note" style={{ color: badgeColor }}>{badge}</div>
    </div>
  );
}

// Known base URLs per provider — used to auto-fill base_url when Sandbox Mode
// is toggled, so an admin never has to hand-type (or mistype) a provider's
// API host. Only providers with well-known REST endpoints are listed; others
// (billplz/toyyibpay have their sandbox/production hosts hardcoded server-side)
// fall back to manual entry.
const PROVIDER_URLS: Record<string, { sandbox: string; production: string }> = {
  hyperswitch: { sandbox: "https://sandbox.hyperswitch.io", production: "https://api.hyperswitch.io" },
  fiuu: { sandbox: "https://sandbox-payment.fiuu.com", production: "https://pay.fiuu.com" },
};

export function PaymentMethodsScreen() {
  const { t } = useTranslation();
  const providerOptions = useMemo(
    () =>
      (
        [
          ["billplz", "providerBillplz"],
          ["toyyibpay", "providerToyyibpay"],
          ["stripe", "providerStripe"],
          ["manual", "providerManual"],
          ["qr_static", "providerQrStatic"],
          ["grabpay", "providerGrabpay"],
          ["boost", "providerBoost"],
          ["tng", "providerTng"],
          ["hyperswitch", "providerHyperswitch"],
          ["fiuu", "providerFiuu"],
          ["custom", "providerCustom"],
        ] as const
      ).map(([value, key]) => ({ value, label: t(`paymentMethods.${key}`) })),
    [t],
  );
  const methodOptions = useMemo(
    () =>
      (
        [
          ["fpx", "methodFpx"],
          ["visa", "methodVisa"],
          ["grabpay", "methodGrabpay"],
          ["cash", "methodCash"],
          ["ewallet", "methodEwallet"],
          ["tng", "methodTng"],
          ["boost", "methodBoost"],
          ["qr", "methodQr"],
        ] as const
      ).map(([value, key]) => ({ value, label: t(`paymentMethods.${key}`) })),
    [t],
  );
  const providerLabel = (v: string) => providerOptions.find(p => p.value === v)?.label ?? v;
  const methodLabel = (v: string) => methodOptions.find(m => m.value === v)?.label ?? v;
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PaymentMethodConfig | null>(null);
  const [form] = Form.useForm();

  // Auto-fill base_url from the known sandbox/production pair whenever the
  // provider or the Sandbox Mode switch changes — an admin should never have
  // to hand-type (or mistype) a gateway's API host.
  const applyKnownUrl = (provider: string | undefined, isSandbox: boolean) => {
    const known = provider ? PROVIDER_URLS[provider] : undefined;
    if (known) form.setFieldValue("base_url", isSandbox ? known.sandbox : known.production);
  };

  const { data, isLoading } = usePaymentMethodConfigs();
  const createConfig = useCreatePaymentMethodConfig();
  const updateConfig = useUpdatePaymentMethodConfig();
  const deleteConfig = useDeletePaymentMethodConfig();
  const testConfig = useTestPaymentMethodConfig();

  const openCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true); };
  const openEdit = (row: PaymentMethodConfig) => {
    setEditing(row);
    form.setFieldsValue({ ...row });
    setModalOpen(true);
  };

  const onSave = async (values: Record<string, unknown>) => {
    try {
      if (editing) {
        await updateConfig.mutateAsync({ id: editing.id, ...values });
        void message.success(t("paymentMethods.updateSuccess"));
      } else {
        await createConfig.mutateAsync(values);
        void message.success(t("paymentMethods.createSuccess"));
      }
      setModalOpen(false);
    } catch { void message.error(t("paymentMethods.saveFailed")); }
  };

  const onDelete = async (id: string) => {
    try { await deleteConfig.mutateAsync(id); void message.success(t("paymentMethods.deleteSuccess")); }
    catch { void message.error(t("paymentMethods.deleteFailed")); }
  };

  const confirmDelete = (row: PaymentMethodConfig) => {
    Modal.confirm({
      title: t("paymentMethods.confirmDelete"),
      okText: t("common.yes"),
      cancelText: t("common.no"),
      danger: true,
      onOk: () => onDelete(row.id),
    });
  };

  const onTest = async (id: string) => {
    try {
      const res = await testConfig.mutateAsync(id);
      const result = (res as { data?: { success: boolean; message: string } }).data;
      if (result?.success) {
        void message.success(t("common.connectionTestOk", { message: result.message }));
      } else {
        void message.error(
          t("common.connectionTestFailed", {
            message: result?.message ?? t("transactions.testFailedGeneric"),
          }),
        );
      }
    } catch { void message.error(t("paymentMethods.testFailed")); }
  };

  const items = (data as { data?: PaymentMethodConfig[] } | undefined)?.data ?? [];
  const activeCount = items.filter(item => item.is_active).length;
  const inactiveCount = items.length - activeCount;
  const sandboxCount = items.filter(item => item.is_sandbox).length;

  const columns: ColumnsType<PaymentMethodConfig> = [
    {
      title: t("paymentMethods.colProvider"), dataIndex: "provider", key: "provider", width: 160,
      render: (v: string) => <span className="admin-data-badge admin-payment-method-provider">{providerLabel(v)}</span>,
    },
    {
      title: t("paymentMethods.colMethod"), dataIndex: "method", key: "method", width: 130,
      render: (v: string) => <span className="admin-data-badge admin-payment-method-type">{methodLabel(v)}</span>,
    },
    {
      title: t("paymentMethods.colDisplayName"), dataIndex: "display_name", key: "display_name",
      render: (v: string) => <span className="admin-data-customer">{v}</span>,
    },
    {
      title: t("paymentMethods.colApiKey"), dataIndex: "api_key", key: "api_key", width: 160,
      render: (v: string | null) => v ? <Text className="admin-data-code">{v}</Text> : <span className="admin-data-secondary">—</span>,
    },
    {
      title: t("deliveryProviders.formSandboxMode", "Sandbox Mode"), dataIndex: "is_sandbox", key: "is_sandbox", width: 110,
      render: (v: boolean) => (
        <span className="admin-data-badge" style={{ color: v ? T.brandAmberText : T.successText, background: v ? T.warningBg : T.successBg }}>
          {v ? t("deliveryProviders.sandbox", "Sandbox") : t("deliveryProviders.production", "Production")}
        </span>
      ),
    },
    {
      title: t("paymentMethods.colQrCode"), dataIndex: "qr_image_url", key: "qr", width: 80,
      render: (v: string | null) => v ? (
        <Tooltip title={<img src={v} alt={t("paymentMethods.qrPreviewAlt")} style={{ width: 120, height: 120 }} />}>
          <span className="admin-payment-method-qr"><span />{t("paymentMethods.colQrBadge")}</span>
        </Tooltip>
      ) : <span className="admin-data-secondary">—</span>,
    },
    {
      title: t("paymentMethods.colStatus"), dataIndex: "is_active", key: "active", width: 90,
      render: (v: boolean) => (
        <span className="admin-data-badge" style={{ color: v ? T.successText : T.textSecondary, background: v ? T.successBg : T.surfaceSection }}>
          {v ? t("common.yes") : t("common.no")}
        </span>
      ),
    },
    {
      title: t("paymentMethods.colActions"), key: "actions", width: 96, fixed: "right" as const,
      render: (_: unknown, row: PaymentMethodConfig) => (
        <Dropdown
          menu={{
            items: [
              { key: "test", icon: <ApiOutlined />, label: t("paymentMethods.testConnection"), onClick: () => void onTest(row.id) },
              { key: "edit", icon: <EditOutlined />, label: t("common.edit"), onClick: () => openEdit(row) },
              { key: "delete", icon: <DeleteOutlined />, label: t("common.delete"), danger: true, onClick: () => confirmDelete(row) },
            ],
          }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            className="admin-payment-method-more admin-data-more"
            type="text"
            aria-label={`${t("paymentMethods.colActions")} ${row.display_name}`}
            loading={testConfig.isPending}
            icon={<MoreOutlined style={{ fontSize: 18 }} />}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <AdminShell>
      <div className="admin-payment-methods-page admin-list-page">
        <div style={{ maxWidth: 1480, margin: "0 auto" }}>
          <div className="admin-payment-methods-toolbar">
            <div>
              <h1 className="admin-page-title">{t("paymentMethods.title")}</h1>
              <p className="admin-payment-methods-subtitle">{t("paymentMethods.subtitle")}</p>
            </div>
            <Button className="admin-payment-methods-create" type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              {t("paymentMethods.addMethod")}
            </Button>
          </div>

          <div className="admin-payment-methods-stats admin-txn-stats grid">
            <PaymentStatCard
              icon={<WalletOutlined />} iconColor={T.brand}
              badge={t("paymentMethods.statBadgeOverall")} badgeColor={T.brandAmberText} badgeBg={T.warningBg}
              label={t("paymentMethods.statTotal")} value={items.length}
            />
            <PaymentStatCard
              icon={<CheckCircleOutlined />} iconColor={T.successBright}
              badge={t("paymentMethods.statBadgeActive")} badgeColor={T.successText} badgeBg={T.successBg}
              label={t("paymentMethods.statActive")} value={activeCount}
            />
            <PaymentStatCard
              icon={<CloseCircleOutlined />} iconColor={T.dangerText}
              badge={t("paymentMethods.statBadgeInactive")} badgeColor={T.dangerText} badgeBg={T.dangerBg}
              label={t("paymentMethods.statInactive")} value={inactiveCount}
            />
            <PaymentStatCard
              icon={<ApiOutlined />} iconColor={T.brand}
              badge={t("paymentMethods.statBadgeSandbox")} badgeColor={T.brandAmberText} badgeBg={T.warningBg}
              label={t("paymentMethods.statSandbox")} value={sandboxCount}
            />
          </div>

          <p className="admin-payment-methods-results">{t("common.total", { count: items.length })}</p>
          <div className="admin-payment-methods-table-panel admin-data-table-panel">
            <DataTable<PaymentMethodConfig>
              columns={columns}
              dataSource={items}
              rowKey="id"
              loading={isLoading}
              pagination={{ pageSize: 10 }}
            />
          </div>
        </div>
      </div>

      <Modal
        open={modalOpen}
        title={
          <div className="admin-payment-method-dialog-title">
            <span><WalletOutlined /></span>
            {editing ? t("paymentMethods.editTitle") : t("paymentMethods.newTitle")}
          </div>
        }
        onCancel={() => setModalOpen(false)}
        onOk={() => void form.submit()}
        okText={t("common.save")}
        okButtonProps={{ style: { background: T.brandAmber, borderColor: T.brandAmber }, loading: createConfig.isPending || updateConfig.isPending }}
        width={580}
        maskClosable={false}
      >
        <Form className="admin-payment-method-form" form={form} layout="vertical" onFinish={onSave} colon={false}>
          <div className="admin-payment-method-section-label">{t("paymentMethods.sectionGeneral")}</div>
          <div className="admin-payment-method-fields">
            <Form.Item name="provider" label={t("paymentMethods.formProvider")} rules={[{ required: true }]}>
              <Select options={providerOptions} placeholder={t("paymentMethods.phSelectProvider")}
                onChange={(v: string) => applyKnownUrl(v, form.getFieldValue("is_sandbox") ?? true)} />
            </Form.Item>
            <Form.Item name="method" label={t("paymentMethods.formMethod")} rules={[{ required: true }]}>
              <Select options={methodOptions} placeholder={t("paymentMethods.phSelectMethod")} />
            </Form.Item>
          </div>
          <Form.Item name="display_name" label={t("paymentMethods.formDisplayName")} rules={[{ required: true }]}>
            <Input placeholder={t("paymentMethods.phDisplayName")} />
          </Form.Item>

          <div className="admin-payment-method-sandbox-card">
            <Form.Item name="is_sandbox" label={t("deliveryProviders.formSandboxMode", "Sandbox Mode")} valuePropName="checked" initialValue={true}
              extra={t("deliveryProviders.sandboxModeHint", "Sandbox uses the gateway's test environment — switch off only once you have real production credentials.")}>
              <Switch checkedChildren={t("deliveryProviders.sandbox", "Sandbox")} unCheckedChildren={t("deliveryProviders.production", "Production")}
                onChange={(checked: boolean) => applyKnownUrl(form.getFieldValue("provider"), checked)} />
            </Form.Item>
          </div>

          <div className="admin-payment-method-section-label">{t("paymentMethods.sectionCredentials")}</div>
          <div className="admin-payment-method-fields">
            <Form.Item name="api_key" label={t("paymentMethods.formApiKey")}>
              <Input.Password placeholder={t("paymentMethods.formApiKeyPlaceholder")} visibilityToggle />
            </Form.Item>
            <Form.Item name="api_secret" label={t("paymentMethods.formApiSecret")}>
              <Input.Password placeholder={t("paymentMethods.formApiSecretPlaceholder")} visibilityToggle />
            </Form.Item>
            <Form.Item name="merchant_id" label={t("paymentMethods.formMerchantId")}>
              <Input placeholder={t("paymentMethods.phMerchantId")} />
            </Form.Item>
            <Form.Item name="collection_id" label={t("paymentMethods.formCollectionId")}>
              <Input placeholder={t("paymentMethods.phCollectionId")} />
            </Form.Item>
            <Form.Item name="webhook_secret" label={t("paymentMethods.formWebhookSecret")}>
              <Input.Password placeholder={t("paymentMethods.formWebhookSecretPlaceholder")} visibilityToggle />
            </Form.Item>
            <Form.Item name="base_url" label={t("deliveryProviders.formBaseUrl")}>
              <Input placeholder={t("deliveryProviders.phBaseUrl")} />
            </Form.Item>
          </div>

          <div className="admin-payment-method-section-label">{t("paymentMethods.sectionDisplay")}</div>
          <Form.Item name="qr_image_url" label={t("paymentMethods.formQrImageUrl")}>
            <Input placeholder={t("paymentMethods.phQrUrl")} />
          </Form.Item>
          <div className="admin-payment-method-fields admin-payment-method-fields-compact">
            <Form.Item name="sort_order" label={t("paymentMethods.formSortOrder")} initialValue={0}>
              <Input type="number" min={0} />
            </Form.Item>
            <Form.Item name="is_active" label={t("paymentMethods.formIsActive")} valuePropName="checked" initialValue={true}>
              <Switch checkedChildren={t("common.yes")} unCheckedChildren={t("common.no")} />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </AdminShell>
  );
}
